import { AppError, normalizeAppError } from "./app-error";

/** Bound the complete operation, including response parsing, even if a transport ignores abort. */
export async function boundedRequest<T>(
  operation: (signal: AbortSignal) => Promise<T>,
  signal: AbortSignal,
  timeoutMs = 5000,
): Promise<T> {
  if (signal.aborted) throw normalizeAppError(undefined, { signal });
  const controller = new AbortController();
  const cancel = () => controller.abort(normalizeAppError(undefined, { signal }));
  signal.addEventListener("abort", cancel, { once: true });
  const timer = setTimeout(() => controller.abort(new AppError("REQUEST_TIMEOUT")), timeoutMs);
  let rejectAbort: () => void = () => {};
  const aborted = new Promise<never>((_resolve, reject) => {
    rejectAbort = () =>
      reject(normalizeAppError(controller.signal.reason, { signal: controller.signal }));
    controller.signal.addEventListener("abort", rejectAbort, { once: true });
  });
  try {
    return await Promise.race([operation(controller.signal), aborted]);
  } catch (error) {
    throw normalizeAppError(error, { signal: controller.signal, fallbackCode: "NETWORK_ERROR" });
  } finally {
    clearTimeout(timer);
    signal.removeEventListener("abort", cancel);
    controller.signal.removeEventListener("abort", rejectAbort);
  }
}
